import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExLoader,
  ExPill,
  LoaderSize,
  LoaderVariant,
  PillColor,
} from '@boomi/exosphere';
import { generateUniqueKey, transformApiResponseToWizardData } from '@services/agent-foundation/agent-foundation-api-service';
import './AgentPreview.scss';
import ProfileSection from '@components/AgentBuilder/ProfileSection/ProfileSection';
import GuardrailSection from '@components/AgentBuilder/GuardrailSection/GuardrailSection';
import TaskSection from '@components/AgentBuilder/TaskSection/TaskSection';
import ConfigurationSection from '@components/AgentBuilder/ConfigurationSection/ConfigurationSection';
import modelsApiService from '@services/agent-foundation/agent-foundation-models-api-service';
import { BoomiModel } from '@components/ModelsManagement/types/models.types';

function AgentPreview({
  importPreviewDetails,
  sources,
  isAgentAlreadyCreated = false,
  isReadyToUseTemplate = false,
  onTemplateCalculated,
}: any) {
  const builderSteps = ['Profile', 'Configuration', 'Tasks', 'Guardrails'];
  const [boomiModels, setBoomiModels] = useState<BoomiModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(true);
  const [t] = useTranslation('common');
  const [selectedBuilderStep, setSelectedBuilderStep] = useState('Profile');
  const handleBuilderStep = (step: any) => {
    setSelectedBuilderStep(step);
  };
  useEffect(() => {
    setIsLoadingModels(true);
    modelsApiService.getBoomiModels().then(setBoomiModels).finally(() => {
      setIsLoadingModels(false);
    });
  }, []);

  useEffect(() => {
    if (importPreviewDetails && boomiModels.length > 0 && onTemplateCalculated) {
      const previewTemplate = transformApiResponseToWizardData(importPreviewDetails, boomiModels);
      onTemplateCalculated(previewTemplate);
    }
  }, [importPreviewDetails, boomiModels, onTemplateCalculated]);
  const renderComponent = () => {
    let template = importPreviewDetails;
    if (template) {
      const previewTemplate = transformApiResponseToWizardData(template, boomiModels);
      // Add unique id to each task if tasks exist and don't already have an id
      if (template?.tasks?.length > 0) {
        template.tasks = template.tasks.map((task: any, idx: number) => ({
          ...task,
          id: task.id || `task-${idx}-${generateUniqueKey(idx)}`,
        }));
      }

      if (template?.agent_mode === 'structured') {
        template = {
          ...template,
          input_json_schema: template.input_schema || '{}',
          output_json_schema: template.output_schema || '{}',
        };
      }

      switch (selectedBuilderStep) {
        case 'Profile':
          return <ProfileSection template={template} />;
        case 'Configuration':
          return <ConfigurationSection template={previewTemplate} />;
        case 'Tasks':
          return (
            <TaskSection
              template={template}
              sources={sources}
              showToolBanner
              isAgentAlreadyCreated={isAgentAlreadyCreated}
              isReadyToUseTemplate={isReadyToUseTemplate}
            />
          );
        case 'Guardrails':
          return <GuardrailSection template={previewTemplate?.guardrails?.data} />;
        default:
          return <ProfileSection template={template} />;
      }
    }
    return null;
  };
  return (
    <>
      <div className="builder-steps-container">
        {builderSteps.map((builderStep: any, index: number) => (
          <ExPill
            key={generateUniqueKey(index)}
            interactive
            selected={builderStep === selectedBuilderStep}
            color={builderStep === selectedBuilderStep ? PillColor.AQUA : undefined}
            onClick={() => handleBuilderStep(builderStep)}
          >
            {builderStep === 'Tasks' ? t('review_panel_task_title') : builderStep}
          </ExPill>
        ))}
      </div>
      <div className="template-preview-section">
        {isLoadingModels && (
          <ExLoader size={LoaderSize.MEDIUM} variant={LoaderVariant.SPINNER} label={t('ai_loading', 'Loading')} />
        )}
        {!isLoadingModels && renderComponent()}
      </div>
    </>
  );
}
export default AgentPreview;
